import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Award, Bell, CalendarDays, CheckCircle2, ChevronRight, Lock, Megaphone, Receipt, UserPlus, Users, Wallet } from 'lucide-react';
import { fmtDate, naira } from '@/lib/format';
import { useAuth } from '@/stores/auth';
import { getHome, getWallet } from '@/api/parent';
import { type ChildCard, ErrorBox, unwrap } from '../ui';

interface Home {
  children: ChildCard[];
  total_owing: number;
  upcoming_events: { id: number; title: string; starts_at: string; type?: string }[];
  unread_notices: number;
  unread_notifications: number;
  pending_link_requests: number | unknown[];
}

const TODAY: Record<string, { text: string; cls: string }> = {
  present: { text: 'In school today', cls: 'bg-green-50 text-green-700' },
  late: { text: 'Came late today', cls: 'bg-amber-50 text-amber-700' },
  absent: { text: 'Absent today', cls: 'bg-red-50 text-red-700' },
  excused: { text: 'Excused today', cls: 'bg-blue-50 text-blue-700' },
};

function Tile({ to, label, value, hint, icon: Icon, tone = 'text-slate-900' }: { to: string; label: string; value: ReactNode; hint?: string; icon: any; tone?: string }) {
  return (
    <Link to={to} className="bg-white rounded-2xl border border-slate-200 p-3.5 hover:border-slate-300 block min-w-0">
      <div className="flex items-center justify-between gap-2"><p className="text-xs text-slate-500 truncate">{label}</p><Icon className="w-4 h-4 text-slate-400 shrink-0" /></div>
      <p className={`text-xl sm:text-2xl font-extrabold mt-1 truncate ${tone}`}>{value}</p>
      {hint && <p className="text-[11px] text-slate-500 truncate">{hint}</p>}
    </Link>
  );
}

function Panel({ title, to, link, children }: { title: string; to?: string; link?: string; children: ReactNode }) {
  return (
    <section className="bg-white rounded-2xl border border-slate-200">
      <div className="flex items-center justify-between px-4 pt-3.5 pb-2"><h2 className="font-bold text-slate-900">{title}</h2>{to && <Link to={to} className="text-xs font-semibold text-[#0A2540] inline-flex items-center">{link ?? 'Open'}<ChevronRight className="w-3.5 h-3.5" /></Link>}</div>
      <div className="px-4 pb-4">{children}</div>
    </section>
  );
}

function ChildTile({ c }: { c: ChildCard }) {
  const t = c.today ? TODAY[c.today] : null;
  const rate = c.attendance_month?.rate;
  return (
    <div className="rounded-2xl border border-slate-200 p-3.5">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-[#0A2540] text-white grid place-items-center font-bold shrink-0">{c.name.charAt(0)}</div>
        <div className="min-w-0 flex-1">
          <Link to={`/parent/children/${c.student_id}`} className="font-bold text-slate-900 truncate block">{c.name}</Link>
          <p className="text-xs text-slate-500 truncate">{c.class ?? 'No class'} · {c.admission_no}</p>
        </div>
        {t ? <span className={`text-[11px] font-semibold px-2 py-1 rounded-full shrink-0 ${t.cls}`}>{t.text}</span>
          : c.status === 'active' ? <span className="text-[11px] text-slate-400 shrink-0">Not marked today</span> : null}
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3 text-center">
        <div className="rounded-xl bg-slate-50 py-2"><p className="text-[10px] text-slate-500">Attendance</p><p className="font-bold text-sm">{rate != null ? `${rate}%` : '—'}</p></div>
        <div className="rounded-xl bg-slate-50 py-2"><p className="text-[10px] text-slate-500">Owing</p><p className={`font-bold text-sm ${c.owing > 0 ? 'text-red-600' : 'text-green-700'}`}>{c.owing > 0 ? naira(c.owing) : 'Nil'}</p></div>
        <div className="rounded-xl bg-slate-50 py-2"><p className="text-[10px] text-slate-500">Results</p><p className={`font-bold text-sm ${c.results_held ? 'text-purple-700' : 'text-slate-900'}`}>{c.results_held ? 'Held' : 'Open'}</p></div>
      </div>
      {c.results_held && <p className="mt-2 text-xs text-purple-800 bg-purple-50 rounded-lg px-2.5 py-1.5"><Lock className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />Results are held until the balance is cleared.</p>}
      <div className="flex gap-2 mt-3 text-xs font-semibold">
        <Link to={`/parent/children/${c.student_id}`} className="flex-1 text-center py-2 rounded-xl border border-slate-200">Progress</Link>
        <Link to={`/parent/results?student=${c.student_id}`} className="flex-1 text-center py-2 rounded-xl border border-slate-200">Results</Link>
        {c.owing > 0 && c.can_pay && <Link to={`/parent/fees?student=${c.student_id}`} className="flex-1 text-center py-2 rounded-xl bg-[#0A2540] text-white">Pay</Link>}
      </div>
    </div>
  );
}

export function DashboardPage() {
  const user = useAuth((s) => s.user);
  const q = useQuery({ queryKey: ['parent-home'], queryFn: () => unwrap<Home>(getHome()).then((h) => ({ ...h, children: h?.children ?? [], upcoming_events: h?.upcoming_events ?? [] })) });
  const w = useQuery({ queryKey: ['parent-wallet'], queryFn: () => getWallet(), retry: false });
  const h = q.data;
  const pending = h ? (Array.isArray(h.pending_link_requests) ? h.pending_link_requests.length : Number(h.pending_link_requests ?? 0)) : 0;
  const first = (user?.name ?? '').split(' ')[0];
  const absentToday = h?.children.filter((c) => c.today === 'absent') ?? [];
  const wallet = w.data;
  const walletReady = wallet && wallet.onboarded && !wallet.requires_bvn;

  return (
    <div className="space-y-4 max-w-6xl">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Parent</p>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">{first ? `Welcome, ${first}` : 'Dashboard'}</h1>
      </div>

      {q.isLoading ? <p className="text-sm text-slate-500">Loading…</p> : q.isError || !h ? <ErrorBox error={q.error} onRetry={() => q.refetch()} /> : (
        <>
          {absentToday.length > 0 && (
            <p className="text-sm bg-red-50 text-red-800 rounded-xl px-3 py-2">{absentToday.map((c) => c.name.split(' ')[0]).join(' and ')} {absentToday.length > 1 ? 'were' : 'was'} marked absent today. If this is a mistake, message the form teacher.</p>
          )}
          {pending > 0 && <p className="text-sm bg-amber-50 text-amber-800 rounded-xl px-3 py-2">{pending} request{pending > 1 ? 's' : ''} to link a child {pending > 1 ? 'are' : 'is'} waiting for the school to confirm.</p>}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Tile to="/parent/children" label="Children" value={h.children.length} icon={Users} />
            <Tile to="/parent/fees" label="Total owing" value={h.total_owing > 0 ? naira(h.total_owing) : 'Nil'} tone={h.total_owing > 0 ? 'text-red-600' : 'text-green-700'} hint={h.total_owing > 0 ? 'Tap to pay' : 'All fees paid'} icon={Receipt} />
            <Tile to="/parent/notices" label="New notices" value={h.unread_notices} icon={Megaphone} />
            <Tile to="/parent/notifications" label="Notifications" value={h.unread_notifications} icon={Bell} />
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <Panel title="My children" to="/parent/children" link="All">
                {!h.children.length ? (
                  <div className="text-center py-6">
                    <UserPlus className="w-8 h-8 text-slate-300 mx-auto" />
                    <p className="text-sm text-slate-500 mt-2">No child is linked to your account yet.</p>
                    <Link to="/parent/children" className="inline-block mt-3 px-4 py-2 rounded-xl bg-[#0A2540] text-white text-sm font-semibold">Link my child</Link>
                  </div>
                ) : <div className="grid sm:grid-cols-2 gap-3">{h.children.map((c) => <ChildTile key={c.student_id} c={c} />)}</div>}
              </Panel>
            </div>

            <div className="space-y-4">
              {wallet && (
                <Panel title="Wallet" to="/parent/wallet" link={walletReady ? 'Open' : 'Set up'}>
                  {walletReady ? (
                    <>
                      <p className="text-2xl font-extrabold">{naira(Number(wallet.balance))}</p>
                      {wallet.funding_account?.account_number && <p className="text-xs text-slate-500 mt-1">Top up: {wallet.funding_account.bank_name} · <span className="font-mono">{wallet.funding_account.account_number}</span></p>}
                      {!wallet.has_pin && <Link to="/parent/wallet" className="mt-2 block text-xs font-semibold text-amber-700">Set your 4-digit PIN to pay from the wallet →</Link>}
                    </>
                  ) : (
                    <div className="flex items-start gap-3"><Wallet className="w-5 h-5 text-slate-400 mt-0.5 shrink-0" /><p className="text-sm text-slate-600">Create a wallet to pay fees in one tap and get your own account number for top-ups. It takes about a minute.</p></div>
                  )}
                </Panel>
              )}

              <Panel title="Coming up" to="/parent/calendar" link="Calendar">
                {!h.upcoming_events?.length ? <p className="text-sm text-slate-500">Nothing in the next 30 days.</p> : (
                  <ul className="space-y-2">
                    {h.upcoming_events.map((e) => (
                      <li key={e.id} className="flex items-center gap-3">
                        <CalendarDays className="w-4 h-4 text-slate-400 shrink-0" />
                        <span className="text-sm flex-1 min-w-0 truncate">{e.title}</span>
                        <span className="text-xs text-slate-500 shrink-0">{fmtDate(e.starts_at)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>

              <Panel title="Quick actions">
                <div className="grid grid-cols-2 gap-2 text-xs font-semibold">
                  <Link to="/parent/fees" className="rounded-xl border border-slate-200 p-3 flex items-center gap-2"><Receipt className="w-4 h-4" />Pay fees</Link>
                  <Link to="/parent/results" className="rounded-xl border border-slate-200 p-3 flex items-center gap-2"><Award className="w-4 h-4" />Results</Link>
                  <Link to="/parent/messages" className="rounded-xl border border-slate-200 p-3 flex items-center gap-2"><Megaphone className="w-4 h-4" />Message teacher</Link>
                  <Link to="/parent/payment-history" className="rounded-xl border border-slate-200 p-3 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" />Receipts</Link>
                </div>
              </Panel>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
